// Diagnostics panel body (IM-2465). The shared run/results/copy UI, used by both
// the standalone /diagnostics route (variant="page") and the footer pop-up modal
// (variant="modal"). Deliberately minimal dependencies: it reads AuthContext
// (app-wide) and the theme, nothing else, so it loads even when the rest of the
// app state is broken. All displayed times are UTC.
import './styles.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FC, useContext, useState } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import pkg from '../../../package.json';
import { ENVIRONMENT } from '../../config/environment';
import AuthContext from '../../providers/auth/auth-service';
import { useThemeMode } from '../../providers/theme-provider';

import { REST_BASE_URL, WS_BASE_URL, deriveVerdict, runAllDiagnostics } from './diagnostics-checks';
import { buildReportText, copyReportToClipboard } from './diagnostics-report';
import type { CheckResult, CheckStatus, Verdict } from './diagnostics-types';

const STATUS_LABEL: Record<CheckStatus, string> = {
  pass: 'Pass',
  warn: 'Warn',
  fail: 'Fail',
};

// A detail value should always be a string, but it is sourced from checks that
// touch app context and backend responses; coerce defensively so an unexpected
// shape can never throw "Objects are not valid as a React child" and take down
// the panel (and, via the app error boundary, the whole app) - IM-2524.
const asText = (value: unknown): string =>
  typeof value === 'string' ? value : value == null ? '' : String(value);

const StatusChip = ({ status }: { status: CheckStatus }) => (
  <span className={`diagnostics-chip diagnostics-chip--${status}`}>{STATUS_LABEL[status]}</span>
);

const VerdictBanner = ({ verdict }: { verdict: Verdict }) => (
  <section className={`diagnostics__verdict diagnostics__verdict--${verdict.status}`}>
    <StatusChip status={verdict.status} />
    <p className="diagnostics__verdict-text">{verdict.headline}</p>
  </section>
);

const ResultCard = ({ result }: { result: CheckResult }) => (
  <section className="diagnostics-card">
    <header className="diagnostics-card__head">
      <h2 className="diagnostics-card__title">{asText(result.title)}</h2>
      <StatusChip status={result.status} />
    </header>
    <p className="diagnostics-card__summary">{asText(result.summary)}</p>
    {result.details.length > 0 && (
      <dl className="diagnostics-kv">
        {result.details.map((d) => (
          <div className="diagnostics-kv__row" key={d.label}>
            <dt className="diagnostics-kv__key">{asText(d.label)}</dt>
            <dd className="diagnostics-kv__val">{asText(d.value)}</dd>
          </div>
        ))}
      </dl>
    )}
    {result.timings.length > 0 && (
      <dl className="diagnostics-kv diagnostics-kv--timings">
        {result.timings.map((t) => (
          <div className="diagnostics-kv__row" key={t.label}>
            <dt className="diagnostics-kv__key">{t.label}</dt>
            <dd className="diagnostics-kv__val">{t.ms == null ? 'n/a' : `${t.ms} ms`}</dd>
          </div>
        ))}
      </dl>
    )}
  </section>
);

interface Props {
  // 'page' fills the viewport for the standalone route; 'modal' drops the
  // full-height shell so it sits inside the footer Dialog.
  variant?: 'page' | 'modal';
  // When provided (modal), renders a close control in the header.
  onClose?: () => void;
}

const DiagnosticsPanelBody: FC<Props> = ({ variant = 'page', onClose }) => {
  // Guard the theme hook: if its provider is not mounted above this panel the
  // hook can return undefined, and destructuring would throw at render (IM-2524).
  const themeMode = useThemeMode();
  const resolvedMode = themeMode?.resolvedMode ?? 'dark';
  const { accessToken } = useContext(AuthContext);

  const [results, setResults] = useState<CheckResult[]>([]);
  const [running, setRunning] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const verdict = results.length > 0 ? deriveVerdict(results) : null;

  const handleRun = async () => {
    setRunning(true);
    setCopied(false);
    setRunError(null);
    try {
      const next = await runAllDiagnostics({
        appBuild: pkg.version,
        accessToken: accessToken || null,
      });
      setResults(next);
      setGeneratedAt(new Date().toISOString());
    } catch (err) {
      // runAllDiagnostics guards each check, but never let an unexpected throw
      // escape the handler - show it in-panel instead (IM-2524).
      setRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const handleCopy = async () => {
    if (results.length === 0) return;
    const text = buildReportText(results, {
      generatedAt,
      environment: ENVIRONMENT,
      appBuild: pkg.version,
      restBase: REST_BASE_URL,
      wsBase: WS_BASE_URL,
      verdict: deriveVerdict(results).headline,
    });
    setCopied(await copyReportToClipboard(text));
  };

  return (
    <div className={`diagnostics diagnostics--${resolvedMode} diagnostics--${variant}`}>
      <header className="diagnostics__header">
        <div>
          <h1 className="diagnostics__title">Diagnostics</h1>
          <p className="diagnostics__subtitle">
            Env {ENVIRONMENT} - Build {pkg.version}
            {generatedAt ? ` - last run ${generatedAt} UTC` : ''}
          </p>
        </div>
        <div className="diagnostics__actions">
          <button
            type="button"
            className="diagnostics__btn diagnostics__btn--primary"
            onClick={handleRun}
            disabled={running}
          >
            {running ? 'Running...' : 'Run diagnostics'}
          </button>
          <button
            type="button"
            className="diagnostics__btn"
            onClick={handleCopy}
            disabled={results.length === 0 || running}
          >
            Copy to clipboard
          </button>
          {copied && <span className="diagnostics__copied">Copied</span>}
          {onClose && (
            <button
              type="button"
              className="diagnostics__btn diagnostics__close"
              onClick={onClose}
              aria-label="Close diagnostics"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </header>

      {runError && (
        <section className="diagnostics__verdict diagnostics__verdict--fail">
          <StatusChip status="fail" />
          <p className="diagnostics__verdict-text">
            Diagnostics could not complete: {asText(runError)}
          </p>
        </section>
      )}

      {results.length === 0 || verdict == null ? (
        !runError && (
          <p className="diagnostics__empty">
            Run the battery to check client environment, auth, WebSocket, REST API, request-phase
            timings and per-backend reachability, then copy a report for support.
          </p>
        )
      ) : (
        <>
          <VerdictBanner verdict={verdict} />
          <div className="diagnostics__results">
            {results.map((r) => (
              <ResultCard key={r.id} result={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// A diagnostics panel must never be able to take down the host app. If anything
// in the body throws at render, contain it here and show the error in place -
// not via the app-level error boundary, which replaces the whole app with the
// error page (IM-2524).
const PanelErrorFallback = ({ error }: FallbackProps) => (
  <div className="diagnostics diagnostics--page">
    <section className="diagnostics__verdict diagnostics__verdict--fail">
      <StatusChip status="fail" />
      <p className="diagnostics__verdict-text">
        Diagnostics hit an unexpected error: {asText(error?.message ?? error)}
      </p>
    </section>
  </div>
);

const DiagnosticsPanel: FC<Props> = (props) => (
  <ErrorBoundary FallbackComponent={PanelErrorFallback}>
    <DiagnosticsPanelBody {...props} />
  </ErrorBoundary>
);

export default DiagnosticsPanel;
